import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getConsent,
  saveConsent,
  acceptAll,
  rejectNonEssential,
} from "../../../utils/cookieConsent";
import "./cookieBanner.scss";

const CookieBanner = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, personalization: false });

  const categories = [
    {
      key: "analytics",
      label: t("cookieBanner.analytics"),
      desc: t("cookieBanner.analyticsDesc"),
    },
    {
      key: "personalization",
      label: t("cookieBanner.personalization"),
      desc: t("cookieBanner.personalizationDesc"),
    },
  ];

  useEffect(() => {
    const existing = getConsent();
    if (!existing) setVisible(true);

    window.daromaOpenCookies = () => {
      const consent = getConsent();
      if (consent) setPrefs({ analytics: !!consent.analytics, personalization: !!consent.personalization });
      setOpen(false);
      setVisible(true);
    };

    return () => { delete window.daromaOpenCookies; };
  }, []);

  const hide = () => { setVisible(false); setOpen(false); };
  const handleAcceptAll = () => { acceptAll(); hide(); };
  const handleReject = () => { rejectNonEssential(); hide(); };
  const handleSave = () => { saveConsent({ technical: true, ...prefs }); hide(); };
  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

  if (!visible) return null;

  return (
    <div className="dcb">

      {/* ── Barra principal ── */}
      <div className="dcb-container">

        <div className="dcb-info">
          <p className="dcb-title">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5"/>
              <path d="M8.5 8.5v.01"/><path d="M16 15.5v.01"/><path d="M12 12v.01"/>
            </svg>
            {t("cookieBanner.title")}
          </p>
          <p className="dcb-desc">
            {t("cookieBanner.desc")}
          </p>
          <div className="dcb-links">
            <Link to="/legal#cookies">{t("cookieBanner.cookiePolicy")}</Link>
            <Link to="/legal#privacidad">{t("cookieBanner.privacy")}</Link>
          </div>
        </div>

        <div className="dcb-actions">
          <button className="dcb-btn dcb-btn--reject" onClick={handleReject}>
            {t("cookieBanner.onlyNecessary")}
          </button>
          <button
            className={`dcb-btn dcb-btn--customize${open ? " dcb-btn--customize-active" : ""}`}
            onClick={() => setOpen((v) => !v)}
          >
            {t("cookieBanner.customize")}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={`dcb-chevron${open ? " dcb-chevron--up" : ""}`}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          <button className="dcb-btn dcb-btn--accept" onClick={handleAcceptAll}>
            {t("cookieBanner.acceptAll")}
          </button>
        </div>

      </div>

      {/* ── Panel de personalización ── */}
      <div className={`dcb-panel${open ? " dcb-panel--open" : ""}`}>
        <div className="dcb-panel-inner">

          <div className="dcb-row">
            <div className="dcb-row-info">
              <span className="dcb-row-name">{t("cookieBanner.technicalCookies")}</span>
              <span className="dcb-row-desc">{t("cookieBanner.technicalDesc")}</span>
            </div>
            <span className="dcb-badge">{t("cookieBanner.alwaysActive")}</span>
          </div>

          {categories.map(({ key, label, desc }) => (
            <div className="dcb-row" key={key}>
              <div className="dcb-row-info">
                <span className="dcb-row-name">{label}</span>
                <span className="dcb-row-desc">{desc}</span>
              </div>
              <label className={`dcb-switch${prefs[key] ? " dcb-switch--on" : ""}`}>
                <input type="checkbox" checked={prefs[key]} onChange={() => toggle(key)} />
                <span className="dcb-switch-track"><span className="dcb-switch-thumb" /></span>
              </label>
            </div>
          ))}

          <div className="dcb-panel-footer">
            <button className="dcb-btn dcb-btn--save" onClick={handleSave}>
              {t("cookieBanner.savePreferences")}
            </button>
          </div>

        </div>
      </div>

      {/* ── Pie ── */}
      <div className="dcb-footer">
        <span>© 2025 Daroma Innovations S.L.</span>
        <div className="dcb-footer-links">
          <Link to="/legal#cookies">{t("cookieBanner.cookiePolicy")}</Link>
          <Link to="/legal#privacidad">{t("cookieBanner.privacy")}</Link>
          <Link to="/legal#aviso-legal">{t("cookieBanner.legalNotice")}</Link>
        </div>
      </div>

    </div>
  );
};

export default CookieBanner;
